/* ============================================
   BYTE-SUB-COLLECTIONS-METAFIELD.JS
   ============================================ */

if (!customElements.get('byte-sub-collections-slider')) {
  class ByteSubCollectionsSlider extends HTMLElement {
    #onClickBound;
    #onScrollBound;
    #onResizeBound;
    #io;
    #ro;
    #lastPageCount;

    constructor() {
      super();
      this.#onClickBound = this.#onClick.bind(this);
      this.#onScrollBound = this.#onScroll.bind(this);
      this.#onResizeBound = this.#onResize.bind(this);
      this.#lastPageCount = null;
      this.#ro = null;
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-byte-sub-collections-viewport]');
      this.track = this.querySelector('.byte-sub-collections-metafield__track-inner');
      this.navBar = this.querySelector('[data-byte-sub-slider-nav]');
      this.dotsHost = this.querySelector('[data-byte-sub-slider-dots]');
      this.prevBtn = this.querySelector('[data-byte-sub-slider-prev]');
      this.nextBtn = this.querySelector('[data-byte-sub-slider-next]');

      this.viewport?.addEventListener('scroll', this.#onScrollBound, { passive: true });
      window.addEventListener('resize', this.#onResizeBound, { passive: true });
      this.addEventListener('click', this.#onClickBound);

      this.#io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.updateNav();
            }
          }
        },
        { threshold: 0.01 }
      );
      this.#io.observe(this);

      if (typeof ResizeObserver !== 'undefined') {
        this.#ro = new ResizeObserver(() => {
          this.#lastPageCount = null;
          this.updateNav();
        });
        if (this.viewport) {
          this.#ro.observe(this.viewport);
        }
        if (this.track) {
          this.#ro.observe(this.track);
        }
      }

      requestAnimationFrame(() => this.updateNav());
      window.setTimeout(() => this.updateNav(), 200);
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this.#onScrollBound);
      window.removeEventListener('resize', this.#onResizeBound);
      this.removeEventListener('click', this.#onClickBound);
      this.#io?.disconnect();
      this.#io = null;
      this.#ro?.disconnect();
      this.#ro = null;
      this.#lastPageCount = null;
      this.viewport = null;
      this.track = null;
      this.navBar = null;
      this.dotsHost = null;
      this.prevBtn = null;
      this.nextBtn = null;
    }

    #getScrollStep() {
      const viewport = this.viewport;
      const track = this.track;
      if (!viewport) {
        return 240;
      }

      const first = track?.querySelector('.byte-sub-collections-metafield__card');
      if (first) {
        const w = first.getBoundingClientRect().width;
        if (w > 1) {
          const styles = track ? window.getComputedStyle(track) : null;
          const gapRaw = styles?.columnGap || styles?.gap || '16px';
          const gap = Number.parseFloat(gapRaw) || 16;
          return Math.round(w + gap);
        }
      }

      const slides = Number.parseInt(
        getComputedStyle(this).getPropertyValue('--byte-sub-collections-slides').trim() || '6',
        10
      );
      const n = Number.isFinite(slides) && slides > 0 ? slides : 6;
      const gapGuess = 18;
      return Math.max(120, Math.round((viewport.clientWidth - (n - 1) * gapGuess) / n + gapGuess));
    }

    #getPagination() {
      const viewport = this.viewport;
      const track = this.track;
      if (!viewport) {
        return { pageCount: 1, maxScroll: 0, step: 1 };
      }

      const contentWidth = track?.scrollWidth ?? viewport.scrollWidth;
      const viewWidth = viewport.clientWidth;
      const maxScroll = Math.max(0, contentWidth - viewWidth);
      let step = this.#getScrollStep();
      const epsilon = 2;

      if (maxScroll <= epsilon) {
        return { pageCount: 1, maxScroll: 0, step: Math.max(1, step) };
      }

      step = Math.max(1, Math.min(step, maxScroll));
      let pageCount = Math.floor(maxScroll / step) + 1;
      if (pageCount < 2) {
        pageCount = 2;
      }
      pageCount = Math.min(30, pageCount);
      return { pageCount, maxScroll, step };
    }

    #buildDots(pageCount) {
      const host = this.dotsHost;
      if (!host) {
        return;
      }

      const labelTpl = this.dataset.slideLabel || 'Go to slide __NUM__';
      host.innerHTML = '';

      for (let i = 0; i < pageCount; i += 1) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'byte-sub-collections-metafield__dot';
        btn.setAttribute('data-byte-sub-dot-index', String(i));
        btn.setAttribute('aria-label', labelTpl.replace('__NUM__', String(i + 1)));
        btn.setAttribute('aria-pressed', 'false');
        host.appendChild(btn);
      }
    }

    #updateActiveDot(pageCount, maxScroll, step) {
      const viewport = this.viewport;
      const host = this.dotsHost;
      if (!viewport || !host || pageCount < 2) {
        return;
      }

      const scrollLeft = viewport.scrollLeft;
      let active = 0;

      if (scrollLeft >= maxScroll - 2) {
        active = pageCount - 1;
      } else {
        active = Math.min(pageCount - 1, Math.max(0, Math.round(scrollLeft / step)));
      }

      const dots = host.querySelectorAll('[data-byte-sub-dot-index]');
      for (let i = 0; i < dots.length; i += 1) {
        const isOn = i === active;
        dots[i].classList.toggle('is-active', isOn);
        dots[i].setAttribute('aria-pressed', isOn ? 'true' : 'false');
      }
    }

    refreshPagination() {
      this.#lastPageCount = null;
      this.updateNav();
    }

    updateNav() {
      const viewport = this.viewport;
      const navBar = this.navBar;
      const prevBtn = this.prevBtn;
      const nextBtn = this.nextBtn;
      const dotsHost = this.dotsHost;

      if (!viewport || !navBar || !prevBtn || !nextBtn || !dotsHost) {
        return;
      }

      const cardCount = this.track?.children?.length ?? 0;
      if (cardCount < 2) {
        navBar.hidden = true;
        return;
      }

      navBar.hidden = false;

      const { pageCount, maxScroll, step } = this.#getPagination();
      const epsilon = 2;

      if (pageCount <= 1 || maxScroll <= epsilon) {
        dotsHost.hidden = true;
        dotsHost.innerHTML = '';
        this.#lastPageCount = null;
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }

      dotsHost.hidden = false;

      if (pageCount !== this.#lastPageCount) {
        this.#buildDots(pageCount);
        this.#lastPageCount = pageCount;
      }

      this.#updateActiveDot(pageCount, maxScroll, step);

      prevBtn.disabled = viewport.scrollLeft <= epsilon;
      nextBtn.disabled = viewport.scrollLeft >= maxScroll - epsilon;
    }

    #scrollByDirection(direction) {
      const viewport = this.viewport;
      if (!viewport) {
        return;
      }

      const step = this.#getScrollStep();
      viewport.scrollBy({
        left: direction * step,
        behavior: 'smooth',
      });

      window.setTimeout(() => this.updateNav(), 350);
    }

    #scrollToDotIndex(index) {
      const viewport = this.viewport;
      if (!viewport) {
        return;
      }

      const { pageCount, maxScroll, step } = this.#getPagination();
      if (pageCount < 2) {
        return;
      }

      const safeIndex = Math.min(pageCount - 1, Math.max(0, index));
      const targetLeft = safeIndex >= pageCount - 1 ? maxScroll : safeIndex * step;

      viewport.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      });

      window.setTimeout(() => this.updateNav(), 350);
    }

    #onClick(event) {
      if (event.target.closest('[data-byte-sub-slider-prev]')) {
        event.preventDefault();
        this.#scrollByDirection(-1);
        return;
      }

      if (event.target.closest('[data-byte-sub-slider-next]')) {
        event.preventDefault();
        this.#scrollByDirection(1);
        return;
      }

      const dot = event.target.closest('[data-byte-sub-dot-index]');
      if (dot && this.contains(dot)) {
        event.preventDefault();
        const raw = dot.getAttribute('data-byte-sub-dot-index');
        const index = Number.parseInt(raw, 10);
        if (!Number.isNaN(index)) {
          this.#scrollToDotIndex(index);
        }
      }
    }

    #onScroll() {
      this.updateNav();
    }

    #onResize() {
      this.#lastPageCount = null;
      this.updateNav();
    }
  }

  customElements.define('byte-sub-collections-slider', ByteSubCollectionsSlider);
}

if (typeof window !== 'undefined' && window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const sliders = event.target?.querySelectorAll?.('byte-sub-collections-slider');
    if (!sliders?.length) {
      return;
    }

    for (const el of sliders) {
      if (typeof el.refreshPagination === 'function') {
        queueMicrotask(() => el.refreshPagination());
      }
    }
  });
}
